import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import PlanningWriteActivityNotice from "./PlanningWriteActivityNotice.jsx";
import DriverAssignmentEditor from "./DriverEmployeeSelect.jsx";
import TractorAssignmentEditor, { TrailerAssignmentEditor } from "./TractorSelect.jsx";
import { MISSING_TRACTOR_LABEL } from "../lib/tractorAssignmentsApi.js";
import React from "react";
import { Link } from "react-router";
import {
  ArrowRightLeft,
  Clock,
  ExternalLink,
  Lock,
  Power,
  Route,
  ShieldCheck,
  PackagePlus,
  Printer,
  Send,
  Smartphone,
  TimerReset,
  Trash2,
  Truck,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import { ActionButton as BaseActionButton, HoverCard, joinClasses } from "./ResultsPrimitives.jsx";
import useAccess from "../hooks/useAccess.jsx";
import { canAccessDriverPortal } from "../lib/accessControl.js";
import { buildDriverPortalHomePath } from "../lib/driverPortal.js";
import { publishLinkedTabAuthBootstrap } from "../lib/tabScopedAuthStorage.js";
import { computeAssignmentApprovalInfo, computeProtectableOrderApprovalInfo, resolveAssignmentDisplayName, resolveEndOfDayLoadTransitReactivationMessage } from "../utils/resultsFleetCardShell.js";
import { isTruckDompeurType } from "../utils/fleetReassignmentHelpers.js";
import {
  buildPortalPublicationActionHoverCardModel,
  findPortalPublicationRoute,
  normalizePortalPublications,
  PORTAL_PUBLICATION_STATUSES,
  resolveTruckPortalPublishDisabledReason,
} from "../lib/portalPublicationsApi.js";
import {
  buildTruckBulkReassignmentTargetOptions,
  buildTruckDispatchDisabledConfirmationContent,
  buildTruckDompeurMixedLoadConfirmationContent,
  buildTruckPreloadTimeConfirmationContent,
  buildTruckOvertimeConfirmationContent,
  buildTruckOrderProtectionConfirmationContent,
  buildTruckProtectionConfirmationContent,
  buildTruckRouteOptimizationConfirmationContent,
  formatTruckPreloadTimeLabel,
  normalizeTruckPreloadTimeValue,
  resolveAssignmentHasPreloadedPreviousDay,
  resolveTruckBulkReassignmentTargetOption,
} from "../utils/resultsWorkspaceData.js";
import { DispatchPublishPortalActionHoverCardContent } from "./resultsPageChrome/DispatchRibbonSection.jsx";

// All write controls in this panel share the planning activity, including open editors.
const ActionButton = React.forwardRef(function PlanningActionButton({ disabled = false, to, ...props }, ref) {
  const busyMessage = usePlanningWriteActivity();
  return <BaseActionButton {...props} ref={ref} to={to} disabled={disabled || (!to && Boolean(busyMessage))} />;
});

function normalizePanelText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveAuthoritativeTruckStartTime(assignment, truckSourceChanges) {
  if (Object.prototype.hasOwnProperty.call(assignment || {}, "data_manager_start_time")) {
    return normalizeTruckPreloadTimeValue(assignment?.data_manager_start_time);
  }
  const truckId = normalizePanelText(assignment?.truck_id);
  const matchingChange = (Array.isArray(truckSourceChanges?.details) ? truckSourceChanges.details : [])
    .find((detail) => normalizePanelText(detail?.truckId || detail?.truck_id) === truckId);
  const startTimeChange = (Array.isArray(matchingChange?.changedFields)
    ? matchingChange.changedFields
    : Array.isArray(matchingChange?.changed_fields)
      ? matchingChange.changed_fields
      : [])
    .find((field) => normalizePanelText(field?.field).toLowerCase() === "start_time");
  if (startTimeChange) {
    return normalizeTruckPreloadTimeValue(startTimeChange.after);
  }
  return normalizeTruckPreloadTimeValue(assignment?.start_time);
}

function normalizeTruckMaxWorkHoursValue(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "" || normalizedValue == null) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }
  return numericValue;
}

function formatTruckMaxWorkHoursLabel(value) {
  const normalizedValue = normalizeTruckMaxWorkHoursValue(value);
  return normalizedValue == null ? "" : `${normalizedValue} h`;
}

function normalizeTruckCapacityTonsValue(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "" || normalizedValue == null) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }
  return numericValue;
}

function formatTruckCapacityTonsLabel(value) {
  const normalizedValue = normalizeTruckCapacityTonsValue(value);
  return normalizedValue == null ? "" : `${normalizedValue} t`;
}

function resolveAuthoritativeTruckCapacityTons(assignment, truckSourceChanges) {
  if (Object.prototype.hasOwnProperty.call(assignment || {}, "data_manager_capacity_tons")) {
    return normalizeTruckCapacityTonsValue(assignment?.data_manager_capacity_tons);
  }

  const truckId = normalizePanelText(assignment?.truck_id);
  const matchingChange = (Array.isArray(truckSourceChanges?.details) ? truckSourceChanges.details : [])
    .find((detail) => normalizePanelText(detail?.truckId || detail?.truck_id) === truckId);
  const capacityTonsChange = (Array.isArray(matchingChange?.changedFields)
    ? matchingChange.changedFields
    : Array.isArray(matchingChange?.changed_fields)
      ? matchingChange.changed_fields
      : [])
    .find((field) => normalizePanelText(field?.field).toLowerCase() === "capacity_tons");

  if (capacityTonsChange) {
    return normalizeTruckCapacityTonsValue(capacityTonsChange.after);
  }
  return normalizeTruckCapacityTonsValue(assignment?.capacity_tons);
}

function resolveAuthoritativeTruckMaxWorkHours(assignment, truckSourceChanges) {
  if (Object.prototype.hasOwnProperty.call(assignment || {}, "data_manager_max_work_hours")) {
    return normalizeTruckMaxWorkHoursValue(assignment?.data_manager_max_work_hours);
  }

  const truckId = normalizePanelText(assignment?.truck_id);
  const matchingChange = (Array.isArray(truckSourceChanges?.details) ? truckSourceChanges.details : [])
    .find((detail) => normalizePanelText(detail?.truckId || detail?.truck_id) === truckId);
  const maxWorkHoursChange = (Array.isArray(matchingChange?.changedFields)
    ? matchingChange.changedFields
    : Array.isArray(matchingChange?.changed_fields)
      ? matchingChange.changed_fields
      : [])
    .find((field) => normalizePanelText(field?.field).toLowerCase() === "max_work_hours");

  if (maxWorkHoursChange) {
    return normalizeTruckMaxWorkHoursValue(maxWorkHoursChange.after);
  }
  return normalizeTruckMaxWorkHoursValue(assignment?.max_work_hours);
}

function formatTruckTimeSettingsMeta(startTimeLabel, maxWorkHoursLabel, capacityTonsLabel) {
  return `Début ${startTimeLabel || "—"} · Max ${maxWorkHoursLabel || "—"} · ${capacityTonsLabel || "—"}`;
}

function TruckActionHoverCardContent({ content }) {
  if (!content || typeof content !== "object") {
    return null;
  }

  const impactItems = Array.isArray(content.impactItems) ? content.impactItems.filter(Boolean) : [];

  return (
    <div className="grid gap-2">
      <div className="text-[0.72rem] font-semibold leading-[1.45] text-content-ink">{content.title || "Action camion"}</div>
      {typeof content.description === "string" && content.description.trim() ? (
        <div className="whitespace-pre-line text-[0.68rem] leading-normal text-content-muted">{content.description}</div>
      ) : null}
      {impactItems.length ? (
        <div className="grid gap-1 text-[0.68rem] leading-normal text-content-muted">
          {impactItems.slice(0, 2).map((item) => (
            <div key={item} className="flex items-start gap-1.5">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-legacy-teal" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const TRUCK_POPOVER_IDLE_HOVER_CLASS_NAME = "hover:bg-surface-accent-tint";

function TruckPopoverSection({ children, title }) {
  return (
    <div className="grid gap-1">
      <div className="px-1 text-[0.68rem] font-semibold uppercase tracking-[0.06em] leading-none text-[rgba(71,85,105,0.7)]">
        {title}
      </div>
      <div className="grid gap-0.5">{children}</div>
    </div>
  );
}

function TruckPortalPublishStatus({ reason = "" }) {
  const normalizedReason = typeof reason === "string" ? reason.trim() : "";
  if (!normalizedReason) {
    return null;
  }

  return (
    <p
      aria-atomic="true"
      aria-live="polite"
      className="m-0 rounded-[4px] border border-[rgba(217,119,6,0.18)] bg-[rgba(255,247,237,0.82)] px-2 py-1.5 text-[0.68rem] font-medium leading-snug text-[rgb(154,52,18)]"
      data-driver-portal-publication-status
      role="status"
    >
      {normalizedReason}
    </p>
  );
}

function TruckPopoverActionRow({
  disabled = false,
  icon: Icon,
  label,
  meta = "",
  onClick = null,
  rel = "",
  target = "",
  title = "",
  tone = "default",
  to = "",
}) {
  const busyMessage = usePlanningWriteActivity();
  disabled ||= !to && Boolean(busyMessage);
  const toneClassName = tone === "success"
    ? "bg-accent-soft text-[var(--color-shell-action-soft-ink)] hover:text-[var(--color-shell-action-soft-ink-hover)]"
    : tone === "accent"
      ? "bg-accent-soft text-[var(--color-shell-action-soft-ink)] hover:text-[var(--color-shell-action-soft-ink-hover)]"
    : tone === "danger"
      ? "bg-[rgba(254,226,226,0.94)] text-[rgb(153,27,27)] hover:bg-[rgba(254,202,202,0.96)]"
      : tone === "warning"
        ? "bg-[rgba(255,247,237,0.92)] text-[rgb(154,52,18)] hover:bg-[rgba(255,237,213,0.96)]"
        : joinClasses(["bg-transparent text-content-ink", TRUCK_POPOVER_IDLE_HOVER_CLASS_NAME]);
  const iconClassName = tone === "success"
    ? "text-[var(--color-shell-action-soft-ink)]"
    : tone === "accent"
      ? "text-[var(--color-shell-action-soft-ink)]"
    : tone === "danger"
      ? "text-[rgb(185,28,28)]"
      : tone === "warning"
        ? "text-[rgb(180,83,9)]"
        : "text-[rgba(71,85,105,0.82)]";
  const className = joinClasses([
    "flex min-h-8 w-full items-center gap-2.5 rounded-[4px] px-2.5 py-1.25 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-1",
    toneClassName,
    disabled
      ? "cursor-not-allowed opacity-45 disabled:hover:bg-transparent"
      : "cursor-pointer",
  ]);
  const content = (
    <>
      {Icon ? <Icon aria-hidden="true" className={joinClasses(["h-4 w-4 shrink-0", iconClassName])} strokeWidth={2.15} /> : null}
      <span className="min-w-0 flex-1 text-[0.84rem] font-medium leading-4.5">{label}</span>
      {meta ? (
        <span className="max-w-[45%] text-right text-[0.68rem] font-semibold leading-snug opacity-85">{meta}</span>
      ) : null}
    </>
  );

  if (to && !disabled) {
    return (
      <Link
        className={className}
        onClick={onClick || undefined}
        rel={rel || undefined}
        target={target || undefined}
        to={to}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      title={title || undefined}
    >
      {content}
    </button>
  );
}

function TruckPreloadTimeClearButton({
  compact = false,
  disabled = false,
  onClick = null,
  title = "",
  truckLabel = "ce camion",
}) {
  const busyMessage = usePlanningWriteActivity();
  disabled ||= Boolean(busyMessage);
  const actionLabel = `Effacer l’heure de préchargement de ${truckLabel}`;

  return (
    <HoverCard
      accentColor="rgb(185,28,28)"
      compactTitle
      content="Retire seulement l’heure manuelle du dispatch courant; le preload et les tournées resteront inchangés."
      delay={120}
      maxWidth={300}
      placement="top"
      referenceAsChild
      tight
      title={actionLabel}
    >
      <button
        type="button"
        aria-label={actionLabel}
        className={joinClasses([
          "inline-flex shrink-0 items-center justify-center rounded-[4px] border border-[rgba(220,38,38,0.22)] bg-[rgba(254,242,242,0.82)] text-[rgb(185,28,28)] transition hover:border-[rgba(185,28,28,0.38)] hover:bg-[rgba(254,226,226,0.92)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[rgba(220,38,38,0.24)] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-45",
          compact ? "h-6 w-6" : "h-8 w-8",
        ])}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        title={title || undefined}
      >
        <Trash2 aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
      </button>
    </HoverCard>
  );
}

export default function TruckProtectionActionPanel({
  assignment = null,
  bulkReassignmentTargets = [],
  className = "",
  compact = false,
  currentDriverName = "",
  dispatchDate = "",
  dispatchPeriod = "day",
  incidents = [],
  onClose = null,
  onManageTruckIncidents = null,
  onOptimizeTruckRoutes = null,
  onPrintTruckSheet = null,
  onPublishTruckPortal = null,
  onReassignAllTruckOrders = null,
  onSetTruckPreloadTime = null,
  onUpdateTruckDriverName = null,
  onUpdateTruckTractor = null,
  onUpdateTruckStartTime = null,
  onUpdateTruckTimeSettings = null,
  onToggleTruckDispatchDisabled = null,
  onToggleTruckDompeurMixedLoad = null,
  onToggleTruckOrdersProtection = null,
  onToggleTruckOvertime = null,
  onToggleTruckProtection = null,
  onUnpublishTruckPortal = null,
  portalPublications = null,
  truckSourceChanges = null,
  truckSheetPrintDisabledReason = "",
  truckSheetPrintIsBusy = false,
  truckTimeSettingsDisabledReason = "",
  variant = "inline",
}) {
  const { access, isReady: isAccessReady } = useAccess();
  const safeAssignment = assignment && typeof assignment === "object" ? assignment : {};
  const truckId = typeof safeAssignment?.truck_id === "string" ? safeAssignment.truck_id.trim() : "";
  // « Camion à choisir » : le menu porte le nom de la remorque (« W27 »), comme la ligne.
  const truckLabel = String(resolveAssignmentDisplayName(safeAssignment) || "").trim() || truckId || "Camion";
  const normalizedDispatchPeriod = normalizePanelText(dispatchPeriod).toLowerCase() === "evening" || normalizePanelText(dispatchPeriod).toLowerCase() === "soir"
    ? "evening"
    : "day";
  const dispatchPeriodLabel = normalizedDispatchPeriod === "evening" ? "Soir" : "Jour";
  const truckIncidents = (Array.isArray(incidents) ? incidents : [])
    .filter((incident) => !normalizePanelText(incident?.truck_id) || normalizePanelText(incident?.truck_id) === truckId);
  const resolvedCurrentDriverName = normalizePanelText(currentDriverName)
    || normalizePanelText(safeAssignment?.driver_name);
  const resolvedCurrentTruckNamePart = normalizePanelText(safeAssignment?.day_truck_name);
  const resolvedCurrentCompartmentName = normalizePanelText(safeAssignment?.compartment_name);
  const driverEditorValueLabel = resolvedCurrentDriverName || "Non renseigné";
  const approvalInfo = computeAssignmentApprovalInfo(safeAssignment?.operational_steps);
  const hasOrders = approvalInfo.total > 0;
  const protectableOrdersInfo = computeProtectableOrderApprovalInfo(safeAssignment?.operational_steps);
  const hasProtectableOrders = protectableOrdersInfo.total > 0;
  const areAllOrdersProtected = hasProtectableOrders && protectableOrdersInfo.approved >= protectableOrdersInfo.total;
  const isDispatchDisabled = Boolean(safeAssignment?.is_dispatch_disabled);
  const endOfDayLoadTransitMessage = resolveEndOfDayLoadTransitReactivationMessage(safeAssignment);
  const isDompeurTruck = isTruckDompeurType(safeAssignment);
  const isDompeurMixedLoadAuthorized = Boolean(safeAssignment?.is_dompeur_mixed_load_authorized);
  const isOvertimeAuthorized = Boolean(safeAssignment?.is_overtime_authorized);
  const isTruckProtected = Boolean(safeAssignment?.is_truck_protected);
  const normalizedPortalPublications = normalizePortalPublications(portalPublications);
  const portalPublicationRoute = findPortalPublicationRoute(normalizedPortalPublications, truckId);
  const hasPortalPublicationSurface = Boolean(
    typeof onPublishTruckPortal === "function"
    || typeof onUnpublishTruckPortal === "function",
  );
  const isPortalPublicationLoading = portalPublications?.isLoading === true;
  const portalPublicationErrorMessage = normalizePanelText(portalPublications?.errorMessage);
  const portalPublicationStatus = portalPublicationRoute?.status || PORTAL_PUBLICATION_STATUSES.unavailable;
  const isPortalPublished = portalPublicationStatus === PORTAL_PUBLICATION_STATUSES.published;
  const isPublishedTruckDispatchDisabled = isPortalPublished
    && (isDispatchDisabled || portalPublicationRoute?.isDispatchDisabled === true);
  const driverPortalPreviewPath = isAccessReady
    && canAccessDriverPortal(access)
    && isPortalPublished
    && normalizedPortalPublications.dispatchDate
    && portalPublicationRoute?.targetDriverUserId
    ? buildDriverPortalHomePath({
        dispatchDate: normalizedPortalPublications.dispatchDate,
        dispatchPeriod: normalizedPortalPublications.dispatchPeriod,
        previewDriverUserId: portalPublicationRoute.targetDriverUserId,
      })
    : "";
  const isPortalPublishGloballyUnavailable = normalizedPortalPublications.publicationAllowed === false;
  const isTruckPortalPublicationAllowed = typeof portalPublicationRoute?.publicationAllowed === "boolean"
    ? portalPublicationRoute.publicationAllowed
    : !isPortalPublishGloballyUnavailable;
  const canPublishTruckPortal = Boolean(
    truckId
    && !isDispatchDisabled
    && !isPortalPublicationLoading
    && !portalPublicationErrorMessage
    && isTruckPortalPublicationAllowed
    && portalPublicationStatus === PORTAL_PUBLICATION_STATUSES.unpublished
    && portalPublicationRoute?.planHash
    && typeof onPublishTruckPortal === "function",
  );
  const canUnpublishTruckPortal = Boolean(
    truckId
    && isPortalPublished
    && typeof onUnpublishTruckPortal === "function",
  );
  const truckPublishDisabledReason = endOfDayLoadTransitMessage && !isPortalPublished
    ? "Camion chargé en PM ce jour-là : aucune tournée à publier sur le portail chauffeur."
    : resolveTruckPortalPublishDisabledReason({
      canPublishTruckPortal,
      isDispatchDisabled,
      isPortalPublished,
      isPortalPublicationLoading,
      portalPublicationErrorMessage,
      portalPublicationRoute,
      publicationUnavailableReason: normalizedPortalPublications.publicationUnavailableReason,
      isPortalPublishGloballyUnavailable,
      portalPublicationStatus,
      truckId,
    });
  const truckPublishHoverCardModel = buildPortalPublicationActionHoverCardModel({
    reason: truckPublishDisabledReason,
    routes: portalPublicationRoute ? [portalPublicationRoute] : [],
    truckIds: truckId ? [truckId] : [],
    truckOutputPolicy: normalizedPortalPublications.truckOutputPolicy,
  });
  const isTruckPublishDisabled = !isPortalPublished && !canPublishTruckPortal;
  const hasPreloadedPreviousDayOrder = resolveAssignmentHasPreloadedPreviousDay(safeAssignment, { excludeEndOfDayLoads: true });
  const isEndOfDayLoadPreloadOnly = !hasPreloadedPreviousDayOrder && resolveAssignmentHasPreloadedPreviousDay(safeAssignment);
  const currentTruckPreloadTime = normalizeTruckPreloadTimeValue(safeAssignment?.truck_preload_time);
  const currentTruckPreloadTimeLabel = formatTruckPreloadTimeLabel(currentTruckPreloadTime);
  const currentTruckStartTime = resolveAuthoritativeTruckStartTime(safeAssignment, truckSourceChanges);
  const currentTruckStartTimeLabel = formatTruckPreloadTimeLabel(currentTruckStartTime);
  const currentTruckMaxWorkHours = resolveAuthoritativeTruckMaxWorkHours(safeAssignment, truckSourceChanges);
  const currentTruckMaxWorkHoursLabel = formatTruckMaxWorkHoursLabel(currentTruckMaxWorkHours);
  const currentTruckCapacityTons = resolveAuthoritativeTruckCapacityTons(safeAssignment, truckSourceChanges);
  const currentTruckCapacityTonsLabel = formatTruckCapacityTonsLabel(currentTruckCapacityTons);
  const currentTruckTimeSettingsMeta = formatTruckTimeSettingsMeta(
    currentTruckStartTimeLabel,
    currentTruckMaxWorkHoursLabel,
    currentTruckCapacityTonsLabel,
  );
  const isOvertimeDisableLocked = Boolean(isOvertimeAuthorized && isTruckProtected && areAllOrdersProtected);
  const truckDispatchHoverCard = buildTruckDispatchDisabledConfirmationContent(truckLabel, isDispatchDisabled, {
    hasOrders,
    isTruckProtected,
  });
  const truckProtectionHoverCard = buildTruckProtectionConfirmationContent(truckLabel, isTruckProtected);
  const truckOrdersProtectionHoverCard = hasProtectableOrders
    ? buildTruckOrderProtectionConfirmationContent(truckLabel, areAllOrdersProtected)
    : null;
  const truckPreloadTimeHoverCard = buildTruckPreloadTimeConfirmationContent(truckLabel, {
    isEligible: hasPreloadedPreviousDayOrder,
    isEndOfDayLoadOnly: isEndOfDayLoadPreloadOnly,
    preloadTime: currentTruckPreloadTime,
  });
  const truckOvertimeHoverCard = buildTruckOvertimeConfirmationContent(truckLabel, isOvertimeAuthorized, {
    isDisableLocked: isOvertimeDisableLocked,
  });
  const truckDompeurMixedLoadHoverCard = isDompeurTruck
    ? buildTruckDompeurMixedLoadConfirmationContent(truckLabel, isDompeurMixedLoadAuthorized, {
      isDompeurTruck,
    })
    : null;
  const truckRouteOptimizationHoverCard = buildTruckRouteOptimizationConfirmationContent(truckLabel, {
    assignment: safeAssignment,
  });
  const truckDriverHoverCard = {
    eyebrow: "Chauffeur",
    title: `Modifier le chauffeur ${dispatchPeriodLabel.toLowerCase()} de ${truckLabel}`,
    description: "Le nom est enregistré dans la table Camions sans invalider le dispatch courant.",
    impactItems: [
      "Jour modifie le chauffeur principal.",
      "Soir modifie le chauffeur soir.",
    ],
  };
  const actionButtonClassName = compact
    ? "w-auto shrink-0 justify-start rounded-sm px-2 font-bold whitespace-nowrap"
    : "w-auto shrink-0 justify-start rounded-sm px-2.25 font-bold whitespace-nowrap";
  const actionButtonStyle = compact
    ? {
        minHeight: "1.72rem",
        paddingTop: "0.14rem",
        paddingBottom: "0.14rem",
        lineHeight: 1,
        fontSize: "0.76rem",
      }
    : {
        minHeight: "1.9rem",
        paddingTop: "0.22rem",
        paddingBottom: "0.22rem",
        lineHeight: 1,
        fontSize: "0.8rem",
      };
  const availableBulkTargets = React.useMemo(
    () => buildTruckBulkReassignmentTargetOptions(truckId, bulkReassignmentTargets),
    [bulkReassignmentTargets, truckId],
  );
  const [isBulkReassignmentOpen, setIsBulkReassignmentOpen] = React.useState(false);
  const [selectedBulkTargetTruckId, setSelectedBulkTargetTruckId] = React.useState("");
  const [isDriverEditorOpen, setIsDriverEditorOpen] = React.useState(false);
  const [isTractorEditorOpen, setIsTractorEditorOpen] = React.useState(false);
  const [isTrailerEditorOpen, setIsTrailerEditorOpen] = React.useState(false);
  const [truckSheetPrintOrientation, setTruckSheetPrintOrientation] = React.useState("portrait");
  const [truckSheetPrintFitOnePage, setTruckSheetPrintFitOnePage] = React.useState(false);
  const [truckSheetPrintShowMap, setTruckSheetPrintShowMap] = React.useState(false);
  const selectedBulkTarget = React.useMemo(
    () => resolveTruckBulkReassignmentTargetOption(truckId, bulkReassignmentTargets, selectedBulkTargetTruckId),
    [bulkReassignmentTargets, selectedBulkTargetTruckId, truckId],
  );
  const canShowBulkReassignmentAction = typeof onReassignAllTruckOrders === "function";
  // Une remorque à « Camion à choisir » n'a pas de chauffeur : il arrive avec le camion.
  const isTractorMissing = safeAssignment.tractor_status === "missing";
  const canUpdateTruckDriverName = Boolean(truckId && typeof onUpdateTruckDriverName === "function" && !isTractorMissing
    && (access?.permissions || []).includes("dispatch.assignment.write"));
  const canUpdateTruckTractor = Boolean(truckId && !isDompeurTruck && typeof onUpdateTruckTractor === "function"
    && (access?.permissions || []).includes("dispatch.assignment.write"));
  const tractorEditorValueLabel = safeAssignment.tractor_status === "missing" ? MISSING_TRACTOR_LABEL : resolvedCurrentTruckNamePart;
  // Une remorque à « Camion à choisir » n'a pas de camion à déplacer.
  const canChangeTrailer = canUpdateTruckTractor && safeAssignment.tractor_status !== "missing";
  // Sans le droit, les actions restent visibles mais grisées (le serveur les refuserait).
  const canWritePlanning = (access?.permissions || []).includes("dispatch.assignment.write");
  const noPlanningRightTitle = canWritePlanning ? "" : "Votre rôle n'a pas ce droit.";
  // Camion protégé : « Réaffecter toutes les commandes » est grisé d'avance, le serveur le refuserait (décision R4).
  const protectedBulkReassignmentTitle = isTruckProtected
    ? "Le camion est protégé : déprotégez-le avant de réaffecter toutes ses commandes."
    : "";
  // Paramètres rapides : heures (fiche du chauffeur) avec « Modifier les chauffeurs », capacité et rayon Soir (remorque)
  // avec « Modifier les remorques » ; un champ sans son droit, ou les heures d'une remorque à « Camion à choisir », est grisé.
  const canWriteDriverHours = (access?.permissions || []).includes("data_manager.employees.write");
  const canWriteTrailerSettings = (access?.permissions || []).includes("data_manager.compartments.write");
  const canWriteTruckTimeSettings = canWritePlanning && (canWriteDriverHours || canWriteTrailerSettings);
  const canUpdateTruckTimeSettings = Boolean(
    truckId
    && canWriteTruckTimeSettings
    && (
      typeof onUpdateTruckTimeSettings === "function"
      || typeof onUpdateTruckStartTime === "function"
    ),
  );

  React.useEffect(() => {
    const nextTargetTruckId = selectedBulkTarget?.truckId || "";
    if (nextTargetTruckId !== selectedBulkTargetTruckId) {
      setSelectedBulkTargetTruckId(nextTargetTruckId);
    }
  }, [selectedBulkTarget?.truckId, selectedBulkTargetTruckId]);

  React.useEffect(() => {
    setIsBulkReassignmentOpen(false);
    setIsDriverEditorOpen(false);
    setIsTractorEditorOpen(false);
    setIsTrailerEditorOpen(false);
    setTruckSheetPrintOrientation("portrait");
    setTruckSheetPrintFitOnePage(false);
    setTruckSheetPrintShowMap(false);
  }, [resolvedCurrentCompartmentName, resolvedCurrentDriverName, resolvedCurrentTruckNamePart, normalizedDispatchPeriod, truckId]);

  React.useEffect(() => {
    if (!isDriverEditorOpen) {
      return;
    }
    setIsBulkReassignmentOpen(false);
    setIsTractorEditorOpen(false);
    setIsTrailerEditorOpen(false);
  }, [isDriverEditorOpen]);

  React.useEffect(() => {
    if (!isTractorEditorOpen) {
      return;
    }
    setIsBulkReassignmentOpen(false);
    setIsDriverEditorOpen(false);
    setIsTrailerEditorOpen(false);
  }, [isTractorEditorOpen]);

  React.useEffect(() => {
    if (!isTrailerEditorOpen) {
      return;
    }
    setIsBulkReassignmentOpen(false);
    setIsDriverEditorOpen(false);
    setIsTractorEditorOpen(false);
  }, [isTrailerEditorOpen]);

  React.useEffect(() => {
    if (!isBulkReassignmentOpen) {
      return;
    }
    setIsDriverEditorOpen(false);
    setIsTractorEditorOpen(false);
    setIsTrailerEditorOpen(false);
  }, [isBulkReassignmentOpen]);

  const exclusionReasons = safeAssignment.optimization_exclusion_reasons || [];
  const productMixingDisabledReason = exclusionReasons.includes("Mélange de produits protégé")
    ? "Mélange de produits protégé : ce camion est exclu de l’optimisation."
    : exclusionReasons.includes("Partage de compartiment protégé")
      ? "Partage de compartiment protégé : ce camion est exclu de l’optimisation."
      : "";
  const canOptimizeTruckRoutes = hasOrders && typeof onOptimizeTruckRoutes === "function" && !productMixingDisabledReason;

  if (variant === "popover") {
    const bulkReassignmentDisabledReason = !hasOrders
      ? "aucune commande"
      : !availableBulkTargets.length
        ? "aucune cible"
        : "";
    const normalizedTruckSheetPrintDisabledReason = normalizePanelText(truckSheetPrintDisabledReason);
    const isTruckSheetPrintDisabled = !truckId
      || typeof onPrintTruckSheet !== "function"
      || truckSheetPrintIsBusy
      || Boolean(normalizedTruckSheetPrintDisabledReason);

    return (
      <div
        className={joinClasses([
          "grid max-h-[calc(100dvh-2rem)] w-[min(27rem,calc(100vw-1.5rem))] gap-2 overflow-y-auto rounded-[4px] border border-[rgba(148,163,184,0.22)] bg-white px-3 py-2.5 text-content-ink shadow-[0_8px_24px_rgba(15,23,42,0.12)]",
          className,
        ])}
        data-truck-protection-panel
        data-truck-protection-panel-variant="popover"
        data-button-visual-scope="menu"
      >
        <div className="flex items-start justify-between gap-2.5 border-b border-[rgba(148,163,184,0.15)] pb-2">
          <div className="flex min-w-0 items-start gap-2.5">
            <span className="mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] bg-[rgba(248,250,252,0.95)] text-content-muted">
              <Truck aria-hidden="true" className="h-4 w-4" strokeWidth={2.2} />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <div className="truncate text-[0.92rem] font-semibold leading-tight text-content-ink">{truckLabel}</div>
              <div className="truncate text-[0.76rem] font-medium leading-tight text-content-muted">
                {resolvedCurrentDriverName || "Chauffeur non renseigné"}
              </div>
            </div>
          </div>
          {typeof onClose === "function" ? (
            <button
              type="button"
              aria-label="Fermer les actions camion"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-[rgba(148,163,184,0.28)] bg-white text-content-muted transition hover:border-[rgba(100,116,139,0.36)] hover:text-content-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-1"
              data-truck-protection-panel-close
              onClick={onClose}
            >
              <X aria-hidden="true" className="h-4 w-4" strokeWidth={2.15} />
            </button>
          ) : null}
        </div>

        <TruckPopoverSection title="rapports">
          <div
            className={joinClasses([
              "flex min-h-8 w-full items-center gap-1 rounded-[4px] bg-transparent transition-colors",
              TRUCK_POPOVER_IDLE_HOVER_CLASS_NAME,
            ])}
            title={normalizedTruckSheetPrintDisabledReason || undefined}
          >
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[4px] px-2.5 py-1.25 text-left text-content-ink transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-45"
              disabled={isTruckSheetPrintDisabled}
              onClick={() => {
                onPrintTruckSheet?.({
                  dispatchDate,
                  dispatchPeriod: normalizedDispatchPeriod,
                  fitOnePage: truckSheetPrintFitOnePage,
                  orientation: truckSheetPrintOrientation,
                  showMap: truckSheetPrintShowMap,
                  truckId,
                  truckName: truckLabel,
                });
              }}
            >
              <Printer aria-hidden="true" className="h-4 w-4 shrink-0 text-[rgba(71,85,105,0.82)]" strokeWidth={2.15} />
              <span className="min-w-0 flex-1 truncate text-[0.84rem] font-medium leading-4.5">
                {truckSheetPrintIsBusy ? "Préparation de l'horaire..." : "Imprimer l'horaire"}
              </span>
            </button>
            <button
              type="button"
              aria-pressed={truckSheetPrintFitOnePage}
              title="Compresse la hauteur pour tenter de tout faire tenir sur une page"
              className={joinClasses([
                "h-7 shrink-0 whitespace-nowrap rounded-[4px] border px-1.5 text-[0.72rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none",
                truckSheetPrintFitOnePage
                  ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
              ])}
              disabled={isTruckSheetPrintDisabled}
              onClick={() => {
                setTruckSheetPrintFitOnePage((current) => !current);
              }}
            >
              Une page
            </button>
            <select
              aria-label="Orientation de l'horaire chauffeur"
              className="h-7 shrink-0 rounded-[4px] border border-line-soft bg-white px-1.5 text-[0.72rem] font-semibold text-content-copy outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-muted"
              disabled={isTruckSheetPrintDisabled}
              value={truckSheetPrintOrientation}
              onChange={(event) => {
                setTruckSheetPrintOrientation(event.target.value === "portrait" ? "portrait" : "landscape");
              }}
            >
              <option value="landscape">Paysage</option>
              <option value="portrait">Portrait</option>
            </select>
            {truckSheetPrintOrientation === "portrait" ? (
              <select
                aria-label="Carte routière de l'horaire chauffeur"
                className="h-7 shrink-0 rounded-[4px] border border-line-soft bg-white px-1.5 text-[0.72rem] font-semibold text-content-copy outline-hidden transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-muted"
                disabled={isTruckSheetPrintDisabled}
                value={truckSheetPrintShowMap ? "map" : "nomap"}
                onChange={(event) => {
                  setTruckSheetPrintShowMap(event.target.value === "map");
                }}
              >
                <option value="map">Carte</option>
                <option value="nomap">Sans carte</option>
              </select>
            ) : null}
          </div>
        </TruckPopoverSection>

        <PlanningWriteActivityNotice />
        <TruckPopoverSection title="aléas">
          <TruckPopoverActionRow
            icon={Wrench}
            label="Bris mécanique / Contretemps"
            meta={truckIncidents.length ? String(truckIncidents.length) : ""}
            disabled={!truckId || typeof onManageTruckIncidents !== "function"}
            tone={truckIncidents.length ? "danger" : "default"}
            onClick={() => {
              onManageTruckIncidents?.({
                dispatchDate,
                dispatchPeriod: normalizedDispatchPeriod,
                incidents: truckIncidents,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
        </TruckPopoverSection>

        <TruckPopoverSection title="protection">
          <TruckPopoverActionRow
            icon={ShieldCheck}
            label={isTruckProtected ? "Déprotéger le camion" : "Protéger le camion"}
            meta={isTruckProtected ? "actif" : ""}
            disabled={!truckId || !canWritePlanning || typeof onToggleTruckProtection !== "function"} title={noPlanningRightTitle}
            tone={isTruckProtected ? "success" : "default"}
            onClick={() => {
              onToggleTruckProtection?.({
                isProtected: isTruckProtected,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
          <TruckPopoverActionRow
            icon={Lock}
            label={areAllOrdersProtected ? "Déprotéger les commandes" : "Protéger les commandes"}
            meta={areAllOrdersProtected ? "actif" : ""}
            disabled={!hasProtectableOrders || !truckId || !canWritePlanning || typeof onToggleTruckOrdersProtection !== "function"}
            title={noPlanningRightTitle || (hasOrders && !hasProtectableOrders ? "Toutes ses commandes sont gelées ou livrées : rien à protéger." : "")}
            tone={areAllOrdersProtected ? "success" : "default"}
            onClick={() => {
              onToggleTruckOrdersProtection?.({
                areAllOrdersProtected,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
          <div className="flex items-stretch gap-1">
            <div className="min-w-0 flex-1">
              <TruckPopoverActionRow
                icon={Clock}
                label={currentTruckPreloadTime ? "Modifier l'heure de préchargement" : "Saisir l'heure de préchargement"}
                meta={currentTruckPreloadTimeLabel || (isEndOfDayLoadPreloadOnly ? "Chargé PM" : "")}
                disabled={!truckId || !canWritePlanning || !hasPreloadedPreviousDayOrder || typeof onSetTruckPreloadTime !== "function"} title={noPlanningRightTitle}
                onClick={() => {
                  onSetTruckPreloadTime?.({
                    hasPreloadedPreviousDayOrder,
                    preloadTime: currentTruckPreloadTime,
                    truckId,
                    truckName: truckLabel,
                  });
                }}
              />
            </div>
            {currentTruckPreloadTime ? (
              <TruckPreloadTimeClearButton
                disabled={!truckId || !canWritePlanning || typeof onSetTruckPreloadTime !== "function"} title={noPlanningRightTitle}
                truckLabel={truckLabel}
                onClick={() => {
                  onSetTruckPreloadTime?.({
                    clearPreloadTime: true,
                    hasPreloadedPreviousDayOrder,
                    preloadTime: currentTruckPreloadTime,
                    truckId,
                    truckName: truckLabel,
                  });
                }}
              />
            ) : null}
          </div>
        </TruckPopoverSection>

        <TruckPopoverSection title="affectation">
          <TruckPopoverActionRow
            icon={UserRound}
            label="Modifier chauffeur"
            meta={driverEditorValueLabel === "Non renseigné" ? "" : driverEditorValueLabel}
            disabled={!canUpdateTruckDriverName} title={isTractorMissing ? "Choisissez d'abord le camion de cette remorque." : noPlanningRightTitle}
            onClick={() => {
                        setIsDriverEditorOpen((currentValue) => !currentValue);
            }}
          />
          {canUpdateTruckDriverName && isDriverEditorOpen ? (
            <DriverAssignmentEditor assignment={safeAssignment} truckId={truckId} currentName={resolvedCurrentDriverName}
              dispatchDate={dispatchDate} dispatchPeriod={normalizedDispatchPeriod}
              onCommit={(payload) => onUpdateTruckDriverName?.({ ...payload, truckName: truckLabel })} />
          ) : null}

          {!isDompeurTruck ? (
            <TruckPopoverActionRow
              icon={Truck}
              label="Changer de camion"
              meta={tractorEditorValueLabel}
              disabled={!canUpdateTruckTractor} title={noPlanningRightTitle}
              onClick={() => setIsTractorEditorOpen((currentValue) => !currentValue)}
            />
          ) : null}
          {canUpdateTruckTractor && isTractorEditorOpen ? (
            <TractorAssignmentEditor truckId={truckId} dispatchDate={dispatchDate} dispatchPeriod={normalizedDispatchPeriod}
              onCommit={(payload) => onUpdateTruckTractor?.({ ...payload, truckName: truckLabel })} />
          ) : null}

          {!isDompeurTruck ? (
            <TruckPopoverActionRow
              icon={ArrowRightLeft}
              label="Changer de remorque"
              meta={resolvedCurrentCompartmentName}
              disabled={!canChangeTrailer} title={noPlanningRightTitle}
              onClick={() => setIsTrailerEditorOpen((currentValue) => !currentValue)}
            />
          ) : null}
          {canChangeTrailer && isTrailerEditorOpen ? (
            <TrailerAssignmentEditor truckId={truckId} dispatchDate={dispatchDate} dispatchPeriod={normalizedDispatchPeriod}
              onCommit={(payload) => onUpdateTruckTractor?.({ ...payload, truckName: truckLabel })} />
          ) : null}

          {typeof onUpdateTruckTimeSettings === "function" || normalizedDispatchPeriod === "day" ? (
            <TruckPopoverActionRow
              icon={Clock}
              label="Paramètres rapides"
              meta={normalizedDispatchPeriod === "evening" ? `Max ${currentTruckMaxWorkHoursLabel || "—"} · Rayon ${safeAssignment.evening_radius_km ?? 40} km` : currentTruckTimeSettingsMeta}
              disabled={!canUpdateTruckTimeSettings || Boolean(truckTimeSettingsDisabledReason)}
              title={canWriteTruckTimeSettings ? truckTimeSettingsDisabledReason : "Votre rôle n'a pas ce droit."}
              onClick={() => {
                const timeSettingsPayload = {
                  dispatchPeriod: normalizedDispatchPeriod,
                  eveningRadiusKm: safeAssignment.evening_radius_km ?? 40,
                  capacityTons: currentTruckCapacityTons,
                  driverName: resolvedCurrentDriverName,
                  trailerName: resolvedCurrentCompartmentName,
                  hoursDisabledReason: isTractorMissing ? "Choisissez d'abord le camion de cette remorque."
                    : canWriteDriverHours ? "" : "Votre rôle n'a pas ce droit.",
                  trailerSettingsDisabledReason: canWriteTrailerSettings ? "" : "Votre rôle n'a pas ce droit.",
                  hasOrders,
                  maxWorkHours: currentTruckMaxWorkHours,
                  startTime: currentTruckStartTime,
                  truckId,
                  truckName: truckLabel,
                };
                if (typeof onUpdateTruckTimeSettings === "function") {
                  onUpdateTruckTimeSettings(timeSettingsPayload);
                  return;
                }
                onUpdateTruckStartTime?.(timeSettingsPayload);
              }}
            />
          ) : null}

          {canShowBulkReassignmentAction ? (
            <>
              <TruckPopoverActionRow
                icon={ArrowRightLeft}
                label="Réaffecter toutes les commandes"
                meta={bulkReassignmentDisabledReason}
                disabled={!hasOrders || !canWritePlanning || isTruckProtected || !availableBulkTargets.length}
                title={noPlanningRightTitle || protectedBulkReassignmentTitle}
                onClick={() => {
                  if (!availableBulkTargets.length) {
                    return;
                  }
                  setIsBulkReassignmentOpen((currentValue) => !currentValue);
                }}
              />
              {hasOrders && isBulkReassignmentOpen ? (
                <div className="grid gap-1.5 rounded-[8px] border border-[rgba(217,119,6,0.22)] bg-[rgba(255,247,237,0.72)] px-2.5 py-2">
                  <label className="text-[0.68rem] font-semibold text-[rgb(154,52,18)]" htmlFor={`bulk-truck-target-${truckId || "default"}`}>
                    Camion cible
                  </label>
                  <select
                    id={`bulk-truck-target-${truckId || "default"}`}
                    className="h-8 rounded-[7px] border border-[rgba(217,119,6,0.24)] bg-white px-2.5 text-[0.78rem] font-medium text-content-ink outline-none transition focus:border-[rgba(217,119,6,0.34)] focus:ring-2 focus:ring-[rgba(245,158,11,0.16)]"
                    value={selectedBulkTarget?.truckId || ""}
                    onChange={(event) => {
                      setSelectedBulkTargetTruckId(event.target.value);
                    }}
                  >
                    {availableBulkTargets.map((targetOption) => (
                      <option key={targetOption.truckId} value={targetOption.truckId} disabled={targetOption.isTractorMissing}>
                        {targetOption.label}
                      </option>
                    ))}
                  </select>
                  {selectedBulkTarget?.meta ? (
                    <div className="text-[0.7rem] leading-snug text-content-muted">{selectedBulkTarget.meta}</div>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ActionButton
                      className="h-7 rounded-[7px] px-2.5 text-[0.72rem]"
                      disabled={!selectedBulkTarget?.truckId}
                      tone="warning"
                      onClick={() => {
                        if (!selectedBulkTarget?.truckId) {
                          return;
                        }
                        setIsBulkReassignmentOpen(false);
                        onReassignAllTruckOrders?.({
                          sourceTruckId: truckId,
                          sourceTruckName: truckLabel,
                          targetTruckId: selectedBulkTarget.truckId,
                          targetTruckName: selectedBulkTarget.truckName || selectedBulkTarget.label,
                        });
                      }}
                    >
                      Confirmer la cible
                    </ActionButton>
                    <button
                      type="button"
                      className="inline-flex h-7 items-center rounded-[7px] border border-[rgba(148,163,184,0.22)] bg-white px-2.5 text-[0.72rem] font-semibold text-content-muted transition hover:border-[rgba(100,116,139,0.32)] hover:text-content-ink"
                      onClick={() => {
                        setIsBulkReassignmentOpen(false);
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {isDompeurTruck ? (
            <HoverCard
              title={truckDompeurMixedLoadHoverCard?.eyebrow || "Regroupement Dompeur"}
              content={<TruckActionHoverCardContent content={truckDompeurMixedLoadHoverCard} />}
              placement="right"
              delay={120}
              maxWidth={390}
              accentColor="var(--color-legacy-teal)"
              referenceAsChild
            >
              <span className="block min-w-0">
                <TruckPopoverActionRow
                  icon={PackagePlus}
                  label={isDompeurMixedLoadAuthorized ? "Désactiver regroupement Dompeur" : "Autoriser regroupement Dompeur"}
                  meta={isDompeurMixedLoadAuthorized ? "actif" : ""}
                  disabled={!truckId || !canWritePlanning || typeof onToggleTruckDompeurMixedLoad !== "function"} title={noPlanningRightTitle}
                  tone={isDompeurMixedLoadAuthorized ? "success" : "default"}
                  onClick={() => {
                    onToggleTruckDompeurMixedLoad?.({
                      isAuthorized: isDompeurMixedLoadAuthorized,
                      isDompeurTruck,
                      truckId,
                      truckName: truckLabel,
                    });
                  }}
                />
              </span>
            </HoverCard>
          ) : null}
        </TruckPopoverSection>

        {hasPortalPublicationSurface ? <TruckPopoverSection title="portail chauffeur">
          {isPortalPublished ? (
            <>
              {driverPortalPreviewPath ? (
                <TruckPopoverActionRow
                  icon={ExternalLink}
                  label="Ouvrir le portail"
                  onClick={() => {
                    publishLinkedTabAuthBootstrap();
                    onClose?.();
                  }}
                  rel="opener"
                  target="_blank"
                  to={driverPortalPreviewPath}
                />
              ) : null}
              <TruckPopoverActionRow
                icon={Smartphone}
                label="Retirer du portail"
                meta={isPublishedTruckDispatchDisabled ? "hors dispatch" : "publié"}
                disabled={!canUnpublishTruckPortal}
                tone="success"
                onClick={() => {
                  onUnpublishTruckPortal?.(portalPublicationRoute);
                }}
              />
            </>
          ) : (
            <>
              <HoverCard
                title="Publier"
                content={<DispatchPublishPortalActionHoverCardContent model={truckPublishHoverCardModel} />}
                placement="right"
                delay={120}
                maxWidth={360}
                accentColor="var(--hovercard-accent-warning)"
                disabled={!truckPublishDisabledReason}
                referenceAsChild
              >
                <span className="block min-w-0">
                  <TruckPopoverActionRow
                    icon={Send}
                    label="Publier"
                    disabled={!canPublishTruckPortal}
                    tone={isTruckPublishDisabled ? "warning" : "default"}
                    onClick={() => {
                      onPublishTruckPortal?.(portalPublicationRoute);
                    }}
                  />
                </span>
              </HoverCard>
              <TruckPortalPublishStatus reason={isTruckPublishDisabled ? truckPublishDisabledReason : ""} />
            </>
          )}
        </TruckPopoverSection> : null}

        <TruckPopoverSection title="optimisation">
          <TruckPopoverActionRow
            icon={Route}
            label="Optimiser ce camion"
            meta={productMixingDisabledReason}
            disabled={!truckId || !canWritePlanning || !canOptimizeTruckRoutes} title={noPlanningRightTitle}
            onClick={() => {
              onOptimizeTruckRoutes?.({
                assignment: safeAssignment,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
          <TruckPopoverActionRow
            icon={TimerReset}
            label={isOvertimeAuthorized ? "Refermer le temps supplémentaire" : "Ouvrir le temps supplémentaire"}
            meta={isOvertimeAuthorized ? "ouvert" : ""}
            disabled={!truckId || !canWritePlanning || typeof onToggleTruckOvertime !== "function" || isOvertimeDisableLocked} title={noPlanningRightTitle}
            tone={isOvertimeAuthorized ? "success" : "default"}
            onClick={() => {
              onToggleTruckOvertime?.({
                isAuthorized: isOvertimeAuthorized,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
        </TruckPopoverSection>

        {endOfDayLoadTransitMessage ? (
          <p
            className="m-0 rounded-[4px] bg-[rgba(254,242,242,0.94)] px-2.5 py-1.5 text-[0.72rem] font-medium leading-snug text-[rgb(153,27,27)]"
            data-end-of-day-load-transit-message
          >
            {endOfDayLoadTransitMessage}
          </p>
        ) : (
          <TruckPopoverActionRow
            icon={Power}
            label={isDispatchDisabled ? "Réactiver dans le dispatch" : "Désactiver pour ce dispatch"}
            disabled={!truckId || !canWritePlanning || typeof onToggleTruckDispatchDisabled !== "function"} title={noPlanningRightTitle}
            tone={isDispatchDisabled ? "success" : "danger"}
            onClick={() => {
              onToggleTruckDispatchDisabled?.({
                hasOrders,
                isDispatchDisabled,
                isTruckProtected,
                truckId,
                truckName: truckLabel,
              });
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={joinClasses([
        "grid gap-1 rounded-[11px] border border-line-soft bg-[rgba(244,250,248,0.92)] px-2 py-1.5 shadow-[0_8px_18px_rgba(15,92,87,0.05)]",
        className,
      ])}
      data-truck-protection-panel
    >
      <div className="grid gap-1">
        <div className="grid gap-0.75">
          <div className="flex items-center justify-between gap-1.5">
            <span className="text-[0.54rem] font-black uppercase tracking-[0.11em] text-[rgba(15,92,87,0.64)]">Protections</span>
            {typeof onClose === "function" ? (
              <button
                type="button"
                className="inline-flex shrink-0 items-center rounded-full border border-line-soft bg-white px-1.75 py-0.5 text-[0.58rem] font-bold leading-none text-content-muted transition hover:border-border-card hover:text-content-ink"
                data-truck-protection-panel-close
                onClick={onClose}
              >
                Fermer
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-1">
            <HoverCard
              title={truckProtectionHoverCard?.eyebrow || "Protection camion"}
              content={<TruckActionHoverCardContent content={truckProtectionHoverCard} />}
              placement="top"
              delay={120}
              maxWidth={360}
              accentColor="var(--color-legacy-teal)"
              referenceAsChild
            >
              <span className="inline-flex max-w-full">
                <ActionButton
                  className={actionButtonClassName}
                  disabled={!truckId || !canWritePlanning || typeof onToggleTruckProtection !== "function"} title={noPlanningRightTitle}
                  style={actionButtonStyle}
                  tone={isTruckProtected ? "subtle" : "accent"}
                  onClick={() => {
                    onToggleTruckProtection?.({
                      isProtected: isTruckProtected,
                      truckId,
                      truckName: truckLabel,
                    });
                  }}
                >
                  {isTruckProtected ? "Déprotéger le camion" : "Protéger le camion"}
                </ActionButton>
              </span>
            </HoverCard>

            {endOfDayLoadTransitMessage ? (
              <p
                className="m-0 max-w-full rounded-[4px] bg-[rgba(254,242,242,0.94)] px-2 py-1 text-[0.62rem] font-medium leading-snug text-[rgb(153,27,27)]"
                data-end-of-day-load-transit-message
              >
                {endOfDayLoadTransitMessage}
              </p>
            ) : (
              <HoverCard
                title={truckDispatchHoverCard?.eyebrow || "Disponibilité dispatch"}
                content={<TruckActionHoverCardContent content={truckDispatchHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!truckId || !canWritePlanning || typeof onToggleTruckDispatchDisabled !== "function"} title={noPlanningRightTitle}
                    style={actionButtonStyle}
                    tone={isDispatchDisabled ? "subtle" : "warning"}
                    onClick={() => {
                      onToggleTruckDispatchDisabled?.({
                        hasOrders,
                        isDispatchDisabled,
                        isTruckProtected,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  >
                    {isDispatchDisabled ? "Réactiver dans le dispatch" : "Désactiver pour ce dispatch"}
                  </ActionButton>
                </span>
              </HoverCard>
            )}

            {hasProtectableOrders ? (
              <HoverCard
                title={truckOrdersProtectionHoverCard?.eyebrow || "Protection commandes"}
                content={<TruckActionHoverCardContent content={truckOrdersProtectionHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!truckId || !canWritePlanning || typeof onToggleTruckOrdersProtection !== "function"} title={noPlanningRightTitle}
                    style={actionButtonStyle}
                    tone={areAllOrdersProtected ? "subtle" : "accent"}
                    onClick={() => {
                      onToggleTruckOrdersProtection?.({
                        areAllOrdersProtected,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  >
                    {areAllOrdersProtected ? "Déprotéger les commandes" : "Protéger les commandes"}
                  </ActionButton>
                </span>
              </HoverCard>
            ) : (
              <div className="flex min-h-6 items-center rounded-sm border border-dashed border-line-soft bg-white/72 px-2.5 text-[0.64rem] font-semibold text-content-muted">
                Aucune commande actuelle à protéger.
              </div>
            )}

            <div className="grid gap-0.5">
              {currentTruckPreloadTime ? (
                <div className="flex items-center gap-1">
                  <span className="pl-0.5 text-[0.58rem] font-black leading-none text-[rgba(8,91,126,0.78)]">
                    {currentTruckPreloadTimeLabel}
                  </span>
                  <TruckPreloadTimeClearButton
                    compact
                    disabled={!truckId || !canWritePlanning || typeof onSetTruckPreloadTime !== "function"} title={noPlanningRightTitle}
                    truckLabel={truckLabel}
                    onClick={() => {
                      onSetTruckPreloadTime?.({
                        clearPreloadTime: true,
                        hasPreloadedPreviousDayOrder,
                        preloadTime: currentTruckPreloadTime,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  />
                </div>
              ) : null}
              <HoverCard
                title={truckPreloadTimeHoverCard?.eyebrow || "Préchargement"}
                content={<TruckActionHoverCardContent content={truckPreloadTimeHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!truckId || !canWritePlanning || !hasPreloadedPreviousDayOrder || typeof onSetTruckPreloadTime !== "function"} title={noPlanningRightTitle}
                    style={actionButtonStyle}
                    tone={currentTruckPreloadTime ? "subtle" : "accent"}
                    onClick={() => {
                      onSetTruckPreloadTime?.({
                        hasPreloadedPreviousDayOrder,
                        preloadTime: currentTruckPreloadTime,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  >
                    {currentTruckPreloadTime ? "Modifier l'heure de préchargement" : "Saisir l'heure de préchargement"}
                  </ActionButton>
                </span>
              </HoverCard>
            </div>

            {/* Masqué quand l'action n'est pas fournie à cet endroit (carte du camion). */}
            {typeof onUpdateTruckDriverName === "function" ? <div className="grid gap-0.5">
              <span className="pl-0.5 text-[0.58rem] font-black leading-none text-[rgba(15,92,87,0.7)]">
                {driverEditorValueLabel}
              </span>
              <HoverCard
                title={truckDriverHoverCard.eyebrow}
                content={<TruckActionHoverCardContent content={truckDriverHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!canUpdateTruckDriverName} title={noPlanningRightTitle}
                    style={actionButtonStyle}
                    tone="accent"
                    onClick={() => {
                                        setIsDriverEditorOpen((currentValue) => !currentValue);
                    }}
                  >
                    {isDriverEditorOpen ? "Masquer chauffeur" : `Modifier chauffeur ${dispatchPeriodLabel}`}
                  </ActionButton>
                </span>
              </HoverCard>
            </div> : null}

          </div>

          {canUpdateTruckDriverName && isDriverEditorOpen ? (
            <DriverAssignmentEditor assignment={safeAssignment} truckId={truckId} currentName={resolvedCurrentDriverName}
              dispatchDate={dispatchDate} dispatchPeriod={normalizedDispatchPeriod}
              onCommit={(payload) => onUpdateTruckDriverName?.({ ...payload, truckName: truckLabel })} />
          ) : null}
        </div>

        <PlanningWriteActivityNotice />
        <div className="grid gap-0.75 border-t border-line-soft pt-1.5">
          {hasPortalPublicationSurface ? <div className="grid gap-0.75 border-b border-line-soft/70 pb-1.5" data-driver-portal-publication-action>
            <span className="text-[0.54rem] font-black uppercase tracking-[0.11em] text-[rgba(15,92,87,0.64)]">Portail chauffeur</span>
            <div className="flex flex-wrap items-center gap-1">
              {driverPortalPreviewPath ? (
                <ActionButton
                  as={Link}
                  className={actionButtonClassName}
                  onClick={() => {
                    publishLinkedTabAuthBootstrap();
                    onClose?.();
                  }}
                  rel="opener"
                  style={actionButtonStyle}
                  target="_blank"
                  to={driverPortalPreviewPath}
                  tone="subtle"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                    <span>Ouvrir le portail</span>
                  </span>
                </ActionButton>
              ) : null}
              <HoverCard
                title="Publier"
                content={<DispatchPublishPortalActionHoverCardContent model={truckPublishHoverCardModel} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--hovercard-accent-warning)"
                disabled={isPortalPublished || !truckPublishDisabledReason}
              >
                <ActionButton
                  className={joinClasses([
                    actionButtonClassName,
                    isTruckPublishDisabled
                      ? "border-[rgba(217,119,6,0.2)]! bg-[rgba(255,247,237,0.94)]! text-[#9a670f]! shadow-none!"
                      : "",
                  ])}
                  disabled={isPortalPublished ? !canUnpublishTruckPortal : !canPublishTruckPortal}
                  style={actionButtonStyle}
                  tone={isPortalPublished ? "subtle" : isTruckPublishDisabled ? "ghost" : "accent"}
                  onClick={() => {
                    if (isPortalPublished) {
                      onUnpublishTruckPortal?.(portalPublicationRoute);
                      return;
                    }
                    onPublishTruckPortal?.(portalPublicationRoute);
                  }}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {isPortalPublished ? <Smartphone aria-hidden="true" className="h-3.5 w-3.5" /> : <Send aria-hidden="true" className="h-3.5 w-3.5" />}
                    <span>{isPortalPublished ? "Retirer du portail" : "Publier"}</span>
                  </span>
                </ActionButton>
              </HoverCard>
              {isPortalPublished ? (
                <span className="text-[0.6rem] font-black uppercase tracking-[0.08em] text-emerald-700">
                  {isPublishedTruckDispatchDisabled ? "Portail actif · camion hors dispatch" : "Publié"}
                </span>
              ) : null}
            </div>
            <TruckPortalPublishStatus reason={isTruckPublishDisabled ? truckPublishDisabledReason : ""} />
          </div> : null}

          <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5 border-b border-line-soft/70 pb-1.5">
            <div className="grid gap-0.75">
              <span className="text-[0.54rem] font-black uppercase tracking-[0.11em] text-[rgba(15,92,87,0.64)]">Routes</span>
              <HoverCard
                title={truckRouteOptimizationHoverCard?.eyebrow || "Optimisation camion"}
                content={<TruckActionHoverCardContent content={truckRouteOptimizationHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!truckId || !canWritePlanning || !canOptimizeTruckRoutes}
                    title={noPlanningRightTitle || productMixingDisabledReason || undefined}
                    style={actionButtonStyle}
                    tone="accent"
                    onClick={() => {
                      onOptimizeTruckRoutes?.({
                        assignment: safeAssignment,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  >
                    Optimiser ce camion
                  </ActionButton>
                </span>
              </HoverCard>
            </div>

            <div className="grid gap-0.75">
              <span className="text-[0.54rem] font-black uppercase tracking-[0.11em] text-[rgba(217,119,6,0.82)]">Heures max</span>
              <HoverCard
                title={truckOvertimeHoverCard?.eyebrow || "Temps supplémentaire"}
                content={<TruckActionHoverCardContent content={truckOvertimeHoverCard} />}
                placement="top"
                delay={120}
                maxWidth={360}
                accentColor="var(--color-legacy-teal)"
                referenceAsChild
              >
                <span className="inline-flex max-w-full">
                  <ActionButton
                    className={actionButtonClassName}
                    disabled={!truckId || !canWritePlanning || typeof onToggleTruckOvertime !== "function" || isOvertimeDisableLocked} title={noPlanningRightTitle}
                    style={actionButtonStyle}
                    tone={isOvertimeAuthorized ? "subtle" : "warning"}
                    onClick={() => {
                      onToggleTruckOvertime?.({
                        isAuthorized: isOvertimeAuthorized,
                        truckId,
                        truckName: truckLabel,
                      });
                    }}
                  >
                    {isOvertimeAuthorized ? "Refermer le temps supplémentaire" : "Ouvrir le temps supplémentaire"}
                  </ActionButton>
                </span>
              </HoverCard>
            </div>
          </div>

          {isDompeurTruck ? (
            <HoverCard
              title={truckDompeurMixedLoadHoverCard?.eyebrow || "Regroupement Dompeur"}
              content={<TruckActionHoverCardContent content={truckDompeurMixedLoadHoverCard} />}
              placement="top"
              delay={120}
              maxWidth={360}
              accentColor="var(--color-legacy-teal)"
              referenceAsChild
            >
              <span className="inline-flex max-w-full">
                <ActionButton
                  className={actionButtonClassName}
                  disabled={!truckId || !canWritePlanning || typeof onToggleTruckDompeurMixedLoad !== "function"} title={noPlanningRightTitle}
                  style={actionButtonStyle}
                  tone={isDompeurMixedLoadAuthorized ? "subtle" : "accent"}
                  onClick={() => {
                    onToggleTruckDompeurMixedLoad?.({
                      isAuthorized: isDompeurMixedLoadAuthorized,
                      isDompeurTruck,
                      truckId,
                      truckName: truckLabel,
                    });
                  }}
                >
                  {isDompeurMixedLoadAuthorized ? "Regroupement Dompeur actif" : "Autoriser regroupement Dompeur"}
                </ActionButton>
              </span>
            </HoverCard>
          ) : null}
        </div>
      </div>
    </div>
  );
}
